import {
  UseQueryOptions,
  UseQueryResult,
  useQuery,
} from '@tanstack/react-query';
import { IFHError } from '../../../../apis/HttpBaseApiWrapper';
import { useAcademicsService } from '../../../../apis/academics/useAcademicsService';
import { IAcademicsAssessmentDetails } from '../../../../apis/academics/academicsAssessments.interfaces';

type TQueryError = IFHError<{ message: string }>;
export const getAcademicsAssessmentDetailsKey =
  'get-academics-assessment-details';

// Helper function to compute scheduledStartDate and scheduledEndDate from subjects
const computeScheduledDates = (
  details: IAcademicsAssessmentDetails
): IAcademicsAssessmentDetails => {
  if (!details.subjects || details.subjects.length === 0) {
    return details;
  }

  // Filter subjects with scheduledDate and sort by date
  const subjectsWithDates = details.subjects
    .filter((s) => s.scheduledDate)
    .sort((a, b) => {
      const dateA = new Date(a.scheduledDate!).getTime();
      const dateB = new Date(b.scheduledDate!).getTime();
      return dateA - dateB;
    });

  if (subjectsWithDates.length === 0) {
    return details;
  }

  return {
    ...details,
    scheduledStartDate: subjectsWithDates[0].scheduledDate,
    scheduledEndDate: subjectsWithDates[subjectsWithDates.length - 1].scheduledDate,
  };
};

export const useAcademicsAssessmentDetailsHook = (
  assessmentId: string,
  options?: UseQueryOptions<IAcademicsAssessmentDetails, TQueryError>
): UseQueryResult<IAcademicsAssessmentDetails, TQueryError> => {
  const { getAcademicsAssessmentDetails } = useAcademicsService();

  return useQuery<IAcademicsAssessmentDetails, TQueryError>({
    queryKey: [getAcademicsAssessmentDetailsKey, assessmentId],
    queryFn: async () => {
      const details = await getAcademicsAssessmentDetails(assessmentId);
      return computeScheduledDates(details);
    },
    enabled: !!assessmentId,
    ...options,
  });
};
